/**
 * SmartTable Component
 * Automatically chooses between regular table and virtual scrolling
 * based on dataset size
 *
 * Features:
 * - Automatic mode selection (virtual vs regular)
 * - Threshold-based switching (default: 100 rows)
 * - Full feature parity between modes
 * - Sorting and filtering support
 * - Row selection
 * - Performance monitoring
 */

import React, { useMemo, useState, useCallback, type ReactNode } from 'react';
import { VirtualTable, type VirtualTableColumn, type SortState } from './VirtualTable';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';
import { logger } from '../utils/logger';
import '../styles/virtual-scroll.css';

// Props interface
export interface SmartTableProps<T> {
  data: T[];
  columns: VirtualTableColumn<T>[];
  rowHeight?: number;
  height?: number | string;
  onRowClick?: (item: T, index: number) => void;
  getRowClassName?: (item: T, index: number) => string;
  keyExtractor: (item: T, index: number) => string | number;
  overscan?: number;
  selectable?: boolean;
  selectedRows?: Set<number>;
  onSelectionChange?: (selectedRows: Set<number>) => void;
  sortable?: boolean;
  defaultSort?: SortState;
  emptyMessage?: string;
  showPerformanceMetrics?: boolean;
  stickyHeader?: boolean;
  className?: string;

  // SmartTable specific props
  virtualThreshold?: number; // Number of rows to trigger virtual scrolling
  forceVirtual?: boolean; // Always use virtual scrolling
  forceRegular?: boolean; // Always use regular table
}

// Regular Table Component (for small datasets)
function RegularTable<T>({
  data,
  columns,
  onRowClick,
  getRowClassName,
  keyExtractor,
  selectable,
  selectedRows,
  onSelectionChange,
  sortable,
  sortState,
  onSortChange,
  emptyMessage,
  stickyHeader,
  className,
}: {
  data: T[];
  columns: VirtualTableColumn<T>[];
  onRowClick?: (item: T, index: number) => void;
  getRowClassName?: (item: T, index: number) => string;
  keyExtractor: (item: T, index: number) => string | number;
  selectable?: boolean;
  selectedRows?: Set<number>;
  onSelectionChange?: (selectedRows: Set<number>) => void;
  sortable?: boolean;
  sortState?: SortState;
  onSortChange?: (sort: SortState) => void;
  emptyMessage?: string;
  stickyHeader?: boolean;
  className?: string;
}) {
  // Handle row selection
  const handleRowSelect = useCallback((index: number, event: React.MouseEvent | React.ChangeEvent) => {
    event.stopPropagation();
    if (!onSelectionChange) return;

    const newSelection = new Set<number>(selectedRows || new Set());
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    onSelectionChange(newSelection);
  }, [selectedRows, onSelectionChange]);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;

    if ((selectedRows?.size || 0) === data.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(data.map((_, i) => i)));
    }
  }, [selectedRows, data, onSelectionChange]);

  // Handle sort
  const handleSort = useCallback((columnKey: string) => {
    if (!sortable || !onSortChange) return;

    const column = columns.find(c => c.key === columnKey);
    if (!column?.sortable) return;

    const newDirection = sortState?.key === columnKey && sortState.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key: columnKey, direction: newDirection });
  }, [sortable, sortState, onSortChange, columns]);

  // Empty state
  if (data.length === 0) {
    return (
      <div className={`virtual-table-container ${className || ''}`}>
        <div className="virtual-scroll-empty">
          <Search className="virtual-scroll-empty-icon" />
          <div className="virtual-scroll-empty-text">{emptyMessage || 'No data available'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`virtual-table-container ${className || ''}`}>
      <div style={{ overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead className={stickyHeader ? 'virtual-table-header' : ''} style={{ position: stickyHeader ? 'sticky' : 'static', top: 0, zIndex: 10 }}>
            <tr style={{ display: 'flex' }}>
              {selectable && (
                <th className="virtual-cell-checkbox" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={(selectedRows?.size || 0) === data.length && data.length > 0}
                    onChange={handleSelectAll}
                    title="Select all"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`virtual-table-header-cell ${column.sortable && sortable ? 'sortable' : ''} ${
                    sortState?.key === column.key ? `sorted ${sortState.direction}` : ''
                  } ${column.headerClassName || ''}`}
                  style={{
                    width: column.width || 'auto',
                    minWidth: column.minWidth,
                    flex: column.width ? 'none' : 1,
                    cursor: column.sortable ? 'pointer' : 'default',
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  {column.header}
                  {sortState?.key === column.key && (
                    sortState.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => {
              const rowClass = getRowClassName ? getRowClassName(item, index) : '';
              const isSelected = selectedRows?.has(index);

              return (
                <tr
                  key={keyExtractor(item, index)}
                  className={`virtual-row ${rowClass} ${isSelected ? 'selected' : ''}`}
                  style={{ display: 'flex', cursor: onRowClick ? 'pointer' : 'default' }}
                  onClick={() => onRowClick?.(item, index)}
                >
                  {selectable && (
                    <td className="virtual-cell-checkbox" style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleRowSelect(index, e)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`virtual-cell ${column.className || ''}`}
                      style={{
                        width: column.width || 'auto',
                        minWidth: column.minWidth,
                        flex: column.width ? 'none' : 1,
                      }}
                    >
                      {column.render
                        ? column.render(item, index)
                        : String((item as Record<string, unknown>)[column.key] ?? '')}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Selection Bar */}
      {selectable && (selectedRows?.size || 0) > 0 && (
        <div className="virtual-scroll-selection-bar">
          <span>{selectedRows?.size} row{selectedRows?.size !== 1 ? 's' : ''} selected</span>
          <button onClick={() => onSelectionChange?.(new Set())}>
            Clear Selection
          </button>
        </div>
      )}
    </div>
  );
}

export function SmartTable<T>({
  data,
  columns,
  rowHeight = 48,
  height = 600,
  onRowClick,
  getRowClassName,
  keyExtractor,
  overscan = 15,
  selectable = false,
  selectedRows,
  onSelectionChange,
  sortable = false,
  defaultSort,
  emptyMessage = 'No data available',
  showPerformanceMetrics = false,
  stickyHeader = true,
  className = '',
  virtualThreshold = 100,
  forceVirtual = false,
  forceRegular = false,
}: SmartTableProps<T>) {
  // Internal selection state if not controlled
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<number>>(new Set());
  const actualSelectedRows = selectedRows ?? internalSelectedRows;
  const actualOnSelectionChange = onSelectionChange ?? setInternalSelectedRows;

  // Internal sort state
  const [sortState, setSortState] = useState<SortState | undefined>(defaultSort);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortState) return data;

    return [...data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[sortState.key];
      const bVal = (b as Record<string, unknown>)[sortState.key];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = aVal < bVal ? -1 : 1;
      return sortState.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortState]);

  // Determine whether to use virtual scrolling
  const useVirtual = useMemo(() => {
    if (forceRegular) return false;
    if (forceVirtual) return true;
    return data.length > virtualThreshold;
  }, [data.length, virtualThreshold, forceVirtual, forceRegular]);

  // Log mode selection for debugging
  React.useEffect(() => {
    if (showPerformanceMetrics) {
      logger.debug(`SmartTable: Using ${useVirtual ? 'virtual' : 'regular'} mode for ${data.length} rows (threshold: ${virtualThreshold})`);
    }
  }, [useVirtual, data.length, virtualThreshold, showPerformanceMetrics]);

  if (useVirtual) {
    return (
      <VirtualTable
        data={sortedData}
        columns={columns}
        rowHeight={rowHeight}
        height={height}
        onRowClick={onRowClick}
        getRowClassName={getRowClassName}
        keyExtractor={keyExtractor}
        overscan={overscan}
        selectable={selectable}
        selectedRows={actualSelectedRows}
        onSelectionChange={actualOnSelectionChange}
        sortable={sortable}
        sortState={sortState}
        onSortChange={setSortState}
        emptyMessage={emptyMessage}
        showPerformanceMetrics={showPerformanceMetrics}
        stickyHeader={stickyHeader}
        className={className}
      />
    );
  }

  return (
    <RegularTable
      data={sortedData}
      columns={columns}
      onRowClick={onRowClick}
      getRowClassName={getRowClassName}
      keyExtractor={keyExtractor}
      selectable={selectable}
      selectedRows={actualSelectedRows}
      onSelectionChange={actualOnSelectionChange}
      sortable={sortable}
      sortState={sortState}
      onSortChange={setSortState}
      emptyMessage={emptyMessage}
      stickyHeader={stickyHeader}
      className={className}
    />
  );
}

export default SmartTable;
